import React, { useEffect, useState } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { Form } from "react-bootstrap";
import ".././patients.css";
import "./PatientProfile.css";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import LogoText from "../../../media/logo_text.webp";

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return "—";
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const calcAgeYears = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return null;
  const parts = isoDate.split("-").map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  const birth = new Date(y, m - 1, d);
  if (Number.isNaN(birth.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate()))
    age -= 1;
  return age >= 0 ? age : null;
};

const LIFE_ANAMNESIS_DEFAULT = {
  general_condition: "",
  operations_injuries: "",
  medications: "",
};

const LIFE_ANAMNESIS_FIELD_LABELS = {
  general_condition: "Общее состояние",
  operations_injuries: "Операции, травмы",
  medications: "Принимаемые препараты",
};

const SOMATIC_DISEASES_DEFAULT = {
  chronic_diseases: "",
  endocrine_status: "",
};

const SOMATIC_DISEASES_FIELD_LABELS = {
  chronic_diseases:
    "В том числе хронические заболевания (отит, синусит, гайморит)",
  endocrine_status: "Эндокринный статус (диабет, состояние щитовидной железы)",
};

const parseLifeAnamnesis = (raw) => {
  if (!raw) return { ...LIFE_ANAMNESIS_DEFAULT };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return {
      general_condition: raw.general_condition ?? "",
      operations_injuries: raw.operations_injuries ?? "",
      medications: raw.medications ?? "",
    };
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { ...LIFE_ANAMNESIS_DEFAULT };
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return {
          general_condition: parsed.general_condition ?? "",
          operations_injuries: parsed.operations_injuries ?? "",
          medications: parsed.medications ?? "",
        };
      }
    } catch (_) {
      return { ...LIFE_ANAMNESIS_DEFAULT };
    }
  }
  return { ...LIFE_ANAMNESIS_DEFAULT };
};

const serializeLifeAnamnesis = (value) =>
  JSON.stringify({
    general_condition: value?.general_condition ?? "",
    operations_injuries: value?.operations_injuries ?? "",
    medications: value?.medications ?? "",
  });

const parseSomaticDiseases = (raw) => {
  if (!raw) return { ...SOMATIC_DISEASES_DEFAULT };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return {
      chronic_diseases: raw.chronic_diseases ?? "",
      endocrine_status: raw.endocrine_status ?? "",
    };
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { ...SOMATIC_DISEASES_DEFAULT };
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return {
          chronic_diseases: parsed.chronic_diseases ?? "",
          endocrine_status: parsed.endocrine_status ?? "",
        };
      }
    } catch (_) {
      return { ...SOMATIC_DISEASES_DEFAULT };
    }
  }
  return { ...SOMATIC_DISEASES_DEFAULT };
};

const serializeSomaticDiseases = (value) =>
  JSON.stringify({
    chronic_diseases: value?.chronic_diseases ?? "",
    endocrine_status: value?.endocrine_status ?? "",
  });

const GYN_ANAMNESIS_DEFAULT = {
  menstruation_age: "",
  regularity: "",
  cycle_duration: "",
  pms: "",
  pregnancy_r: "",
  pregnancy_a: "",
  pregnancy_v: "",
  menstrual_symptoms: "",
  menopause_age: "",
  hormonal_drugs: "",
};

const GYN_ANAMNESIS_FIELD_LABELS = {
  menstruation_age: "Менструация с",
  regularity: "Регулярность менструаций",
  cycle_duration: "Длительность цикла",
  pms: "Предменструальный синдром",
  pregnancy: "Беременность",
  menstrual_symptoms: "Наличие пред-, пери-, постменструальных симптомов",
  menopause_age: "Менопауза с",
  hormonal_drugs: "Прием гормональных препаратов, в т.ч. контрацептивов",
  diseases: "Гинекологические заболевания",
};

const parseGynAnamnesis = (raw) => {
  if (!raw) return { ...GYN_ANAMNESIS_DEFAULT };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return {
      menstruation_age: raw.menstruation_age ?? "",
      regularity: raw.regularity ?? "",
      cycle_duration: raw.cycle_duration ?? "",
      pms: raw.pms ?? "",
      pregnancy_r: raw.pregnancy_r ?? "",
      pregnancy_a: raw.pregnancy_a ?? "",
      pregnancy_v: raw.pregnancy_v ?? "",
      menstrual_symptoms: raw.menstrual_symptoms ?? "",
      menopause_age: raw.menopause_age ?? "",
      hormonal_drugs: raw.hormonal_drugs ?? "",
    };
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { ...GYN_ANAMNESIS_DEFAULT };
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return {
          menstruation_age: parsed.menstruation_age ?? "",
          regularity: parsed.regularity ?? "",
          cycle_duration: parsed.cycle_duration ?? "",
          pms: parsed.pms ?? "",
          pregnancy_r: parsed.pregnancy_r ?? "",
          pregnancy_a: parsed.pregnancy_a ?? "",
          pregnancy_v: parsed.pregnancy_v ?? "",
          menstrual_symptoms: parsed.menstrual_symptoms ?? "",
          menopause_age: parsed.menopause_age ?? "",
          hormonal_drugs: parsed.hormonal_drugs ?? "",
        };
      }
    } catch (_) {
      return { ...GYN_ANAMNESIS_DEFAULT };
    }
  }
  return { ...GYN_ANAMNESIS_DEFAULT };
};

const serializeGynAnamnesis = (value) =>
  JSON.stringify({
    menstruation_age: value?.menstruation_age ?? "",
    regularity: value?.regularity ?? "",
    cycle_duration: value?.cycle_duration ?? "",
    pms: value?.pms ?? "",
    pregnancy_r: value?.pregnancy_r ?? "",
    pregnancy_a: value?.pregnancy_a ?? "",
    pregnancy_v: value?.pregnancy_v ?? "",
    menstrual_symptoms: value?.menstrual_symptoms ?? "",
    menopause_age: value?.menopause_age ?? "",
    hormonal_drugs: value?.hormonal_drugs ?? "",
  });

const ALLERGY_ANAMNESIS_KEYS = [
  "medications",
  "cosmetics",
  "food",
  "household",
  "plants",
  "sun",
  "cold",
];

const YES_NO_ANAMNESIS_ITEM_DEFAULT = { answer: "", note: "" };

const ALLERGY_ANAMNESIS_FIELD_LABELS = {
  medications: "Лекарственные препараты",
  cosmetics: "Косметические средства",
  food: "Пищевые продукты",
  household: "Бытовые аллергены",
  plants: "Растения",
  sun: "Солнце",
  cold: "Холод",
};

const EPID_ANAMNESIS_KEYS = [
  "hepatitis",
  "hiv",
  "syphilis",
  "tuberculosis",
  "herpes",
  "other_infectious",
  "std",
];

const EPID_ANAMNESIS_FIELD_LABELS = {
  hepatitis: "Гепатит",
  hiv: "ВИЧ",
  syphilis: "Сифилис",
  tuberculosis: "Туберкулез",
  herpes: "Герпес",
  other_infectious: "Др. инфекционные заболевания",
  std: "ЗППП",
};

const normalizeYesNoAnamnesisItem = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...YES_NO_ANAMNESIS_ITEM_DEFAULT };
  }
  const answer = raw.answer === "yes" || raw.answer === "no" ? raw.answer : "";
  return {
    answer,
    note: raw.note ?? "",
  };
};

const parseYesNoAnamnesis = (raw, keys) => {
  const empty = () =>
    Object.fromEntries(
      keys.map((k) => [k, { ...YES_NO_ANAMNESIS_ITEM_DEFAULT }]),
    );

  if (!raw) return empty();

  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return empty();
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        return empty();
      source = parsed;
    } catch (_) {
      return empty();
    }
  }

  if (typeof source !== "object" || Array.isArray(source)) return empty();

  return Object.fromEntries(
    keys.map((k) => [k, normalizeYesNoAnamnesisItem(source[k])]),
  );
};

const serializeYesNoAnamnesis = (value, keys) =>
  JSON.stringify(
    Object.fromEntries(
      keys.map((k) => [
        k,
        {
          answer:
            value?.[k]?.answer === "yes" || value?.[k]?.answer === "no"
              ? value[k].answer
              : "",
          note: value?.[k]?.note ?? "",
        },
      ]),
    ),
  );

const parseAllergyAnamnesis = (raw) =>
  parseYesNoAnamnesis(raw, ALLERGY_ANAMNESIS_KEYS);
const serializeAllergyAnamnesis = (value) =>
  serializeYesNoAnamnesis(value, ALLERGY_ANAMNESIS_KEYS);
const parseEpidAnamnesis = (raw) =>
  parseYesNoAnamnesis(raw, EPID_ANAMNESIS_KEYS);
const serializeEpidAnamnesis = (value) =>
  serializeYesNoAnamnesis(value, EPID_ANAMNESIS_KEYS);

const NUTRITION_ANAMNESIS_DEFAULT = {
  meal_regime: "",
  meals_per_day: "",
  food_character: [],
  fluid_liters: "",
  smoking_alcohol: "",
};

const NUTRITION_FOOD_CHARACTER_KEYS = [
  "balanced",
  "protein",
  "vegetarian",
  "high_fat",
  "fast_food",
  "high_carb",
  "spicy",
  "smoked",
  "salty",
  "sweet",
  "soda",
];

const NUTRITION_FOOD_CHARACTER_LABELS = {
  balanced: "Сбалансированный",
  protein: "Преимущественно белковая",
  vegetarian: "Вегетарианство",
  high_fat: "Большое количество жиров",
  fast_food: "Фаст-фуд",
  high_carb: "Большое количество углеводов",
  spicy: "Острое",
  smoked: "Копченое",
  salty: "Соленое",
  sweet: "Сладкое",
  soda: "Газированные напитки",
};

const NUTRITION_ANAMNESIS_FIELD_LABELS = {
  meal_regime: "Режим питания",
  meals_per_day: "Кратность приема пищи",
  food_character: "Характер пищи",
  fluid_liters: "Количество потребляемой жидкости в день",
  smoking_alcohol: "Курение, Алкоголь (количество)",
};

const normalizeNutritionFoodCharacter = (raw) => {
  if (!Array.isArray(raw)) return [];
  return NUTRITION_FOOD_CHARACTER_KEYS.filter((key) => raw.includes(key));
};

const parseNutritionAnamnesis = (raw) => {
  if (!raw) return { ...NUTRITION_ANAMNESIS_DEFAULT, food_character: [] };
  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { ...NUTRITION_ANAMNESIS_DEFAULT, food_character: [] };
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return { ...NUTRITION_ANAMNESIS_DEFAULT, food_character: [] };
      }
      source = parsed;
    } catch (_) {
      return { ...NUTRITION_ANAMNESIS_DEFAULT, food_character: [] };
    }
  }
  if (typeof source !== "object" || Array.isArray(source)) {
    return { ...NUTRITION_ANAMNESIS_DEFAULT, food_character: [] };
  }
  const mealRegime =
    source.meal_regime === "regular" || source.meal_regime === "irregular"
      ? source.meal_regime
      : "";
  const smokingAlcohol = Object.prototype.hasOwnProperty.call(
    source,
    "smoking_alcohol",
  )
    ? (source.smoking_alcohol ?? "")
    : [source.smoking, source.alcohol]
        .map((v) => (v == null ? "" : String(v).trim()))
        .filter(Boolean)
        .join(", ");
  return {
    meal_regime: mealRegime,
    meals_per_day: source.meals_per_day ?? "",
    food_character: normalizeNutritionFoodCharacter(source.food_character),
    fluid_liters: source.fluid_liters ?? "",
    smoking_alcohol: smokingAlcohol,
  };
};

const serializeNutritionAnamnesis = (value) =>
  JSON.stringify({
    meal_regime:
      value?.meal_regime === "regular" || value?.meal_regime === "irregular"
        ? value.meal_regime
        : "",
    meals_per_day: value?.meals_per_day ?? "",
    food_character: normalizeNutritionFoodCharacter(value?.food_character),
    fluid_liters: value?.fluid_liters ?? "",
    smoking_alcohol: value?.smoking_alcohol ?? "",
  });

const formatNutritionFoodCharacterForLog = (keys) =>
  normalizeNutritionFoodCharacter(keys)
    .map((key) => NUTRITION_FOOD_CHARACTER_LABELS[key])
    .filter(Boolean)
    .join(", ");

const DAILY_ROUTINE_DEFAULT = {
  sleep_hours: "",
  sleep_character: [],
  awakening: [],
  awakening_time: "",
  physical_activity: "",
  activity_peak: "",
  sports: "",
  stress_level: "",
};

const DAILY_ROUTINE_SLEEP_CHARACTER_KEYS = ["calm", "restless", "dreams"];
const DAILY_ROUTINE_SLEEP_CHARACTER_LABELS = {
  calm: "Спокойный",
  restless: "Беспокойный",
  dreams: "Сновидения",
};

const DAILY_ROUTINE_AWAKENING_KEYS = ["full_rest", "fatigue", "spontaneous"];
const DAILY_ROUTINE_AWAKENING_LABELS = {
  full_rest: "Ощущение полноценного отдыха",
  fatigue: "Ощущение разбитости, усталости",
  spontaneous: "Спонтанное пробуждение в определенное время, в ___ ч.",
};

const formatAwakeningForLog = (awakening, awakeningTime) => {
  const time = String(awakeningTime ?? "").trim();
  return normalizeCheckboxKeys(awakening, DAILY_ROUTINE_AWAKENING_KEYS)
    .map((key) => {
      if (key !== "spontaneous") return DAILY_ROUTINE_AWAKENING_LABELS[key];
      return time
        ? `Спонтанное пробуждение в определенное время, в ${time} ч.`
        : DAILY_ROUTINE_AWAKENING_LABELS.spontaneous;
    })
    .filter(Boolean)
    .join(", ");
};

const DAILY_ROUTINE_ACTIVITY_KEYS = ["very_low", "low", "medium", "high"];
const DAILY_ROUTINE_ACTIVITY_LABELS = {
  very_low: "очень низкая",
  low: "низкая",
  medium: "средняя",
  high: "высокая",
};

const DAILY_ROUTINE_FIELD_LABELS = {
  sleep_hours: "Сон",
  sleep_character: "Характер сна",
  awakening: "Пробуждение",
  physical_activity: "Физическая активность в течение дня",
  activity_peak: "Пик активности в течение суток",
  sports: "Занятие спортом, физические нагрузки",
  stress_level: "Субъективная оценка уровня стресса",
};

const normalizeCheckboxKeys = (raw, allowedKeys) => {
  if (!Array.isArray(raw)) return [];
  return allowedKeys.filter((key) => raw.includes(key));
};

const formatCheckboxKeysForLog = (keys, labels) =>
  (Array.isArray(keys) ? keys : [])
    .map((key) => labels[key])
    .filter(Boolean)
    .join(", ");

const parseDailyRoutineAnamnesis = (raw) => {
  const empty = () => ({
    ...DAILY_ROUTINE_DEFAULT,
    sleep_character: [],
    awakening: [],
  });
  if (!raw) return empty();
  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return empty();
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        return empty();
      source = parsed;
    } catch (_) {
      return empty();
    }
  }
  if (typeof source !== "object" || Array.isArray(source)) return empty();

  const physicalActivity = DAILY_ROUTINE_ACTIVITY_KEYS.includes(
    source.physical_activity,
  )
    ? source.physical_activity
    : "";
  const sports =
    source.sports === "yes" || source.sports === "no" ? source.sports : "";

  return {
    sleep_hours: source.sleep_hours ?? "",
    sleep_character: normalizeCheckboxKeys(
      source.sleep_character,
      DAILY_ROUTINE_SLEEP_CHARACTER_KEYS,
    ),
    awakening: normalizeCheckboxKeys(
      source.awakening,
      DAILY_ROUTINE_AWAKENING_KEYS,
    ),
    awakening_time: source.awakening_time ?? "",
    physical_activity: physicalActivity,
    activity_peak: source.activity_peak ?? "",
    sports,
    stress_level: source.stress_level ?? "",
  };
};

const serializeDailyRoutineAnamnesis = (value) =>
  JSON.stringify({
    sleep_hours: value?.sleep_hours ?? "",
    sleep_character: normalizeCheckboxKeys(
      value?.sleep_character,
      DAILY_ROUTINE_SLEEP_CHARACTER_KEYS,
    ),
    awakening: normalizeCheckboxKeys(
      value?.awakening,
      DAILY_ROUTINE_AWAKENING_KEYS,
    ),
    awakening_time: value?.awakening_time ?? "",
    physical_activity: DAILY_ROUTINE_ACTIVITY_KEYS.includes(
      value?.physical_activity,
    )
      ? value.physical_activity
      : "",
    activity_peak: value?.activity_peak ?? "",
    sports:
      value?.sports === "yes" || value?.sports === "no" ? value.sports : "",
    stress_level: value?.stress_level ?? "",
  });

const ANTHROPOMETRY_DEFAULT = {
  weight_kg: "",
  height_cm: "",
  body_type: "",
  weight_fluctuation_3_5_years: "",
  weight_fluctuation_1_year: "",
  volume_redistribution: "",
};

const ANTHROPOMETRY_BODY_TYPE_KEYS = [
  "normosthenic",
  "hypersthenic",
  "asthenic",
];
const ANTHROPOMETRY_BODY_TYPE_LABELS = {
  normosthenic: "нормостеник",
  hypersthenic: "гиперстеник",
  asthenic: "астеник",
};

const ANTHROPOMETRY_REDISTRIBUTION_KEYS = ["android", "gynoid", "mixed"];
const ANTHROPOMETRY_REDISTRIBUTION_LABELS = {
  android: "андроидное",
  gynoid: "ганоидное",
  mixed: "смешанное",
};

const ANTHROPOMETRY_FIELD_LABELS = {
  weight_kg: "Масса тела (кг)",
  height_cm: "Рост (см)",
  body_type: "Телосложение",
  weight_fluctuation_3_5_years: "Колебания веса за последние 3-5 лет (кг)",
  weight_fluctuation_1_year: "Колебания веса за последний год (кг)",
  bmi: "ИМС (Индекс массы тела)",
  volume_redistribution: "Перераспределение объемов тела по типу",
};

const calcBmi = (weightKg, heightCm) => {
  const weight = Number.parseFloat(String(weightKg ?? "").replace(",", "."));
  const height = Number.parseFloat(String(heightCm ?? "").replace(",", "."));
  if (
    !Number.isFinite(weight) ||
    !Number.isFinite(height) ||
    weight <= 0 ||
    height <= 0
  ) {
    return "";
  }
  const bmi = weight / (height / 100) ** 2;
  return (Math.round(bmi * 10) / 10).toFixed(1);
};

const parseAnthropometryAnamnesis = (raw) => {
  if (!raw) return { ...ANTHROPOMETRY_DEFAULT };
  let source = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { ...ANTHROPOMETRY_DEFAULT };
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return { ...ANTHROPOMETRY_DEFAULT };
      }
      source = parsed;
    } catch (_) {
      return { ...ANTHROPOMETRY_DEFAULT };
    }
  }
  if (typeof source !== "object" || Array.isArray(source))
    return { ...ANTHROPOMETRY_DEFAULT };

  return {
    weight_kg: source.weight_kg ?? "",
    height_cm: source.height_cm ?? "",
    body_type: ANTHROPOMETRY_BODY_TYPE_KEYS.includes(source.body_type)
      ? source.body_type
      : "",
    weight_fluctuation_3_5_years: source.weight_fluctuation_3_5_years ?? "",
    weight_fluctuation_1_year: source.weight_fluctuation_1_year ?? "",
    volume_redistribution: ANTHROPOMETRY_REDISTRIBUTION_KEYS.includes(
      source.volume_redistribution,
    )
      ? source.volume_redistribution
      : "",
  };
};

const serializeAnthropometryAnamnesis = (value) =>
  JSON.stringify({
    weight_kg: value?.weight_kg ?? "",
    height_cm: value?.height_cm ?? "",
    body_type: ANTHROPOMETRY_BODY_TYPE_KEYS.includes(value?.body_type)
      ? value.body_type
      : "",
    weight_fluctuation_3_5_years: value?.weight_fluctuation_3_5_years ?? "",
    weight_fluctuation_1_year: value?.weight_fluctuation_1_year ?? "",
    bmi: calcBmi(value?.weight_kg, value?.height_cm),
    volume_redistribution: ANTHROPOMETRY_REDISTRIBUTION_KEYS.includes(
      value?.volume_redistribution,
    )
      ? value.volume_redistribution
      : "",
  });

const formatYesNoAnamnesisItemForLog = (item) => {
  const answerMap = { yes: "Да", no: "Нет" };
  const answer = answerMap[item?.answer] || "";
  const note = (item?.note ?? "").trim();
  if (answer && note) return `${answer}: ${note}`;
  if (answer) return answer;
  if (note) return note;
  return "";
};

const FIELD_LABELS = {
  biological_age: "Биологический возраст",
  cardiovascular_risk: "Риск сердечно-сосудистых заболеваний",
  chest_circumference_cm: "Объем груди (см)",
  waist_circumference_cm: "Объем талии (см)",
  hips_circumference_cm: "Объем бедер (см)",
  min_weight_kg: "Минимальный вес (кг)",
  current_weight_kg: "Текущий вес (кг)",
  comfortable_weight_kg: "Комфортный вес (кг)",
  blood_tests: "Эпид анамнез",
  blood_pressure: "Артериальное давление",
  medical_summary: "Краткий эпикриз",
  allergies: "Аллергические реакции",
  diagnosis: "Диагноз",
  complaints: "Жалобы",
  life_anamnesis: "Анамнез жизни",
  somatic_diseases: "Общие соматические заболевания",
  gyn_anamnesis: "Гинекологический анамнез",
  allergy_anamnesis: "Аллергический анамнез",
  nutrition_anamnesis: "Характер питания",
  daily_routine_anamnesis: "Распорядок дня",
  anthropometry_anamnesis: "Антропометрические показатели",
  epid_anamnesis: "Эпидемиологический анамнез",
  permanent_therapy: "Постоянная терапия",
  doctor_comment: "Комментарий врача",
  cardiovascular_diseases: "Заболевания сердечно-сосудистой системы",
  cns_diseases: "Заболевания ЦНС",
  respiratory_diseases: "Заболевания органов дыхания",
  digestive_diseases: "Заболевания органов пищеварения",
  vision_impairments: "Нарушения зрения",
  past_conditions: "Перенесенные состояния",
  heredity: "Наследственность",
  gynecological_history: "Гинекологические заболевания",
  thyroid_disease: "Заболевания щитовидной железы",
  bad_habits: "Вредные привычки",
  operations: "Операции",
  injuries: "Травмы",
};

const PROFILE_FIELD_LABELS = {
  fio: "ФИО",
  cart: "Номер карты",
  doc_id: "Лечащий врач",
  ...FIELD_LABELS,
};

// --- Ключи для UI (логи в БД остаются на русском) ---
const LIFE_ANAMNESIS_FIELD_KEYS = {
  general_condition: "anamnesis.life.generalCondition",
  operations_injuries: "anamnesis.life.operationsInjuries",
  medications: "anamnesis.life.medications",
};

const SOMATIC_DISEASES_FIELD_KEYS = {
  chronic_diseases: "anamnesis.somatic.chronicDiseases",
  endocrine_status: "anamnesis.somatic.endocrineStatus",
};

const ALLERGY_ANAMNESIS_FIELD_KEYS = {
  medications: "anamnesis.allergy.medications",
  cosmetics: "anamnesis.allergy.cosmetics",
  food: "anamnesis.allergy.food",
  household: "anamnesis.allergy.household",
  plants: "anamnesis.allergy.plants",
  sun: "anamnesis.allergy.sun",
  cold: "anamnesis.allergy.cold",
};

const EPID_ANAMNESIS_FIELD_KEYS = {
  hepatitis: "anamnesis.epid.hepatitis",
  hiv: "anamnesis.epid.hiv",
  syphilis: "anamnesis.epid.syphilis",
  tuberculosis: "anamnesis.epid.tuberculosis",
  herpes: "anamnesis.epid.herpes",
  other_infectious: "anamnesis.epid.otherInfectious",
  std: "anamnesis.epid.std",
};

const NUTRITION_FOOD_CHARACTER_LABEL_KEYS = {
  balanced: "anamnesis.nutrition.food.balanced",
  protein: "anamnesis.nutrition.food.protein",
  vegetarian: "anamnesis.nutrition.food.vegetarian",
  high_fat: "anamnesis.nutrition.food.highFat",
  fast_food: "anamnesis.nutrition.food.fastFood",
  high_carb: "anamnesis.nutrition.food.highCarb",
  spicy: "anamnesis.nutrition.food.spicy",
  smoked: "anamnesis.nutrition.food.smoked",
  salty: "anamnesis.nutrition.food.salty",
  sweet: "anamnesis.nutrition.food.sweet",
  soda: "anamnesis.nutrition.food.soda",
};

const DAILY_ROUTINE_SLEEP_CHARACTER_LABEL_KEYS = {
  calm: "anamnesis.routine.sleep.calm",
  restless: "anamnesis.routine.sleep.restless",
  dreams: "anamnesis.routine.sleep.dreams",
};

const DAILY_ROUTINE_AWAKENING_LABEL_KEYS = {
  full_rest: "anamnesis.routine.wake.fullRest",
  fatigue: "anamnesis.routine.wake.fatigue",
  spontaneous: "anamnesis.routine.wake.spontaneous",
};

const DAILY_ROUTINE_ACTIVITY_LABEL_KEYS = {
  very_low: "anamnesis.routine.activity.veryLow",
  low: "anamnesis.routine.activity.low",
  medium: "anamnesis.routine.activity.medium",
  high: "anamnesis.routine.activity.high",
};

const ANTHROPOMETRY_BODY_TYPE_LABEL_KEYS = {
  normosthenic: "anamnesis.anthropometry.bodyTypeOptions.normosthenic",
  hypersthenic: "anamnesis.anthropometry.bodyTypeOptions.hypersthenic",
  asthenic: "anamnesis.anthropometry.bodyTypeOptions.asthenic",
};

const ANTHROPOMETRY_REDISTRIBUTION_LABEL_KEYS = {
  android: "anamnesis.anthropometry.redistributionOptions.android",
  gynoid: "anamnesis.anthropometry.redistributionOptions.gynoid",
  mixed: "anamnesis.anthropometry.redistributionOptions.mixed",
};

const FIELD_LABEL_KEYS = {
  biological_age: "profile.fields.biologicalAge",
  cardiovascular_risk: "profile.fields.cvRisk",
  chest_circumference_cm: "profile.fields.chestCircumference",
  waist_circumference_cm: "profile.fields.waistCircumference",
  hips_circumference_cm: "profile.fields.hipsCircumference",
  min_weight_kg: "profile.fields.minWeight",
  current_weight_kg: "profile.fields.currentWeight",
  comfortable_weight_kg: "profile.fields.comfortableWeight",
  blood_tests: "profile.fields.epidAnamnesis",
  blood_pressure: "profile.fields.bloodPressure",
  medical_summary: "profile.fields.medicalSummary",
  allergies: "profile.fields.allergies",
  diagnosis: "profile.fields.diagnosis",
  complaints: "profile.fields.complaints",
  life_anamnesis: "profile.groups.lifeAnamnesis",
  somatic_diseases: "profile.fields.somaticDiseases",
  gyn_anamnesis: "profile.fields.gynAnamnesis",
  allergy_anamnesis: "profile.fields.allergyAnamnesis",
  nutrition_anamnesis: "profile.fields.nutritionAnamnesis",
  daily_routine_anamnesis: "profile.fields.dailyRoutineAnamnesis",
  anthropometry_anamnesis: "profile.fields.anthropometryAnamnesis",
  epid_anamnesis: "profile.fields.epidAnamnesis",
  permanent_therapy: "profile.fields.permanentTherapy",
  doctor_comment: "profile.fields.doctorComment",
  cardiovascular_diseases: "profile.fields.cardiovascularDiseases",
  cns_diseases: "profile.fields.cnsDiseases",
  respiratory_diseases: "profile.fields.respiratoryDiseases",
  digestive_diseases: "profile.fields.digestiveDiseases",
  vision_impairments: "profile.fields.visionImpairments",
  past_conditions: "profile.fields.pastConditions",
  heredity: "profile.fields.heredity",
  gynecological_history: "anamnesis.gyn.diseases",
  thyroid_disease: "profile.fields.thyroidDisease",
  bad_habits: "profile.fields.badHabits",
  operations: "profile.fields.operations",
  injuries: "profile.fields.injuries",
};

const humanizeFieldKeyLocalized = (key, t) =>
  FIELD_LABEL_KEYS[key] ? t(FIELD_LABEL_KEYS[key]) : String(key).replace(/_/g, " ");

const normalizeComparableValue = (value) => {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).trim();
};

const collectChangedFields = (before, after, labels) => {
  const nestedJsonKeys = [
    "life_anamnesis",
    "somatic_diseases",
    "gyn_anamnesis",
    "allergy_anamnesis",
    "nutrition_anamnesis",
    "daily_routine_anamnesis",
    "anthropometry_anamnesis",
    "epid_anamnesis",
  ];
  const changes = Object.keys(labels).reduce((acc, key) => {
    if (nestedJsonKeys.includes(key)) return acc;
    const fromValue = normalizeComparableValue(before?.[key]);
    const toValue = normalizeComparableValue(after?.[key]);
    if (fromValue !== toValue) {
      acc.push({
        label: labels[key],
        from: before?.[key],
        to: after?.[key],
      });
    }
    return acc;
  }, []);

  Object.keys(LIFE_ANAMNESIS_FIELD_LABELS).forEach((key) => {
    const fromValue = normalizeComparableValue(before?.life_anamnesis?.[key]);
    const toValue = normalizeComparableValue(after?.life_anamnesis?.[key]);
    if (fromValue !== toValue) {
      changes.push({
        label: LIFE_ANAMNESIS_FIELD_LABELS[key],
        from: before?.life_anamnesis?.[key],
        to: after?.life_anamnesis?.[key],
      });
    }
  });

  Object.keys(SOMATIC_DISEASES_FIELD_LABELS).forEach((key) => {
    const fromValue = normalizeComparableValue(before?.somatic_diseases?.[key]);
    const toValue = normalizeComparableValue(after?.somatic_diseases?.[key]);
    if (fromValue !== toValue) {
      changes.push({
        label: SOMATIC_DISEASES_FIELD_LABELS[key],
        from: before?.somatic_diseases?.[key],
        to: after?.somatic_diseases?.[key],
      });
    }
  });

  const gynLogKeys = [
    "menstruation_age",
    "regularity",
    "cycle_duration",
    "pms",
    "menstrual_symptoms",
    "menopause_age",
    "hormonal_drugs",
  ];
  gynLogKeys.forEach((key) => {
    let fromRaw = before?.gyn_anamnesis?.[key];
    let toRaw = after?.gyn_anamnesis?.[key];
    if (key === "regularity") {
      const map = { regular: "регулярно", irregular: "нерегулярно" };
      fromRaw = map[fromRaw] || fromRaw;
      toRaw = map[toRaw] || toRaw;
    }
    const fromValue = normalizeComparableValue(fromRaw);
    const toValue = normalizeComparableValue(toRaw);
    if (fromValue !== toValue) {
      changes.push({
        label: GYN_ANAMNESIS_FIELD_LABELS[key],
        from: fromRaw,
        to: toRaw,
      });
    }
  });

  const pregnancyBefore = [
    before?.gyn_anamnesis?.pregnancy_r,
    before?.gyn_anamnesis?.pregnancy_a,
    before?.gyn_anamnesis?.pregnancy_v,
  ].join("|");
  const pregnancyAfter = [
    after?.gyn_anamnesis?.pregnancy_r,
    after?.gyn_anamnesis?.pregnancy_a,
    after?.gyn_anamnesis?.pregnancy_v,
  ].join("|");
  if (pregnancyBefore !== pregnancyAfter) {
    changes.push({
      label: GYN_ANAMNESIS_FIELD_LABELS.pregnancy,
      from: `Р${before?.gyn_anamnesis?.pregnancy_r || ""} А${before?.gyn_anamnesis?.pregnancy_a || ""} В${before?.gyn_anamnesis?.pregnancy_v || ""}`,
      to: `Р${after?.gyn_anamnesis?.pregnancy_r || ""} А${after?.gyn_anamnesis?.pregnancy_a || ""} В${after?.gyn_anamnesis?.pregnancy_v || ""}`,
    });
  }

  ALLERGY_ANAMNESIS_KEYS.forEach((key) => {
    const fromRaw = formatYesNoAnamnesisItemForLog(
      before?.allergy_anamnesis?.[key],
    );
    const toRaw = formatYesNoAnamnesisItemForLog(
      after?.allergy_anamnesis?.[key],
    );
    if (normalizeComparableValue(fromRaw) !== normalizeComparableValue(toRaw)) {
      changes.push({
        label: ALLERGY_ANAMNESIS_FIELD_LABELS[key],
        from: fromRaw,
        to: toRaw,
      });
    }
  });

  EPID_ANAMNESIS_KEYS.forEach((key) => {
    const fromRaw = formatYesNoAnamnesisItemForLog(
      before?.epid_anamnesis?.[key],
    );
    const toRaw = formatYesNoAnamnesisItemForLog(after?.epid_anamnesis?.[key]);
    if (normalizeComparableValue(fromRaw) !== normalizeComparableValue(toRaw)) {
      changes.push({
        label: EPID_ANAMNESIS_FIELD_LABELS[key],
        from: fromRaw,
        to: toRaw,
      });
    }
  });

  const nutritionLogKeys = [
    "meal_regime",
    "meals_per_day",
    "food_character",
    "fluid_liters",
    "smoking_alcohol",
  ];
  nutritionLogKeys.forEach((key) => {
    let fromRaw = before?.nutrition_anamnesis?.[key];
    let toRaw = after?.nutrition_anamnesis?.[key];
    if (key === "meal_regime") {
      const map = { regular: "Регулярный", irregular: "Нерегулярный" };
      fromRaw = map[fromRaw] || fromRaw;
      toRaw = map[toRaw] || toRaw;
    }
    if (key === "food_character") {
      fromRaw = formatNutritionFoodCharacterForLog(fromRaw);
      toRaw = formatNutritionFoodCharacterForLog(toRaw);
    }
    const fromValue = normalizeComparableValue(fromRaw);
    const toValue = normalizeComparableValue(toRaw);
    if (fromValue !== toValue) {
      changes.push({
        label: NUTRITION_ANAMNESIS_FIELD_LABELS[key],
        from: fromRaw,
        to: toRaw,
      });
    }
  });

  const dailyRoutineLogKeys = [
    "sleep_hours",
    "sleep_character",
    "awakening",
    "physical_activity",
    "activity_peak",
    "sports",
    "stress_level",
  ];
  dailyRoutineLogKeys.forEach((key) => {
    let fromRaw = before?.daily_routine_anamnesis?.[key];
    let toRaw = after?.daily_routine_anamnesis?.[key];
    if (key === "sleep_character") {
      fromRaw = formatCheckboxKeysForLog(
        fromRaw,
        DAILY_ROUTINE_SLEEP_CHARACTER_LABELS,
      );
      toRaw = formatCheckboxKeysForLog(
        toRaw,
        DAILY_ROUTINE_SLEEP_CHARACTER_LABELS,
      );
    }
    if (key === "awakening") {
      fromRaw = formatAwakeningForLog(
        fromRaw,
        before?.daily_routine_anamnesis?.awakening_time,
      );
      toRaw = formatAwakeningForLog(
        toRaw,
        after?.daily_routine_anamnesis?.awakening_time,
      );
    }
    if (key === "physical_activity") {
      fromRaw = DAILY_ROUTINE_ACTIVITY_LABELS[fromRaw] || fromRaw;
      toRaw = DAILY_ROUTINE_ACTIVITY_LABELS[toRaw] || toRaw;
    }
    if (key === "sports") {
      const map = { yes: "Да", no: "Нет" };
      fromRaw = map[fromRaw] || fromRaw;
      toRaw = map[toRaw] || toRaw;
    }
    const fromValue = normalizeComparableValue(fromRaw);
    const toValue = normalizeComparableValue(toRaw);
    if (fromValue !== toValue) {
      changes.push({
        label: DAILY_ROUTINE_FIELD_LABELS[key],
        from: fromRaw,
        to: toRaw,
      });
    }
  });

  const anthropometryLogKeys = [
    "weight_kg",
    "height_cm",
    "body_type",
    "weight_fluctuation_3_5_years",
    "weight_fluctuation_1_year",
    "bmi",
    "volume_redistribution",
  ];
  anthropometryLogKeys.forEach((key) => {
    let fromRaw =
      key === "bmi"
        ? calcBmi(
            before?.anthropometry_anamnesis?.weight_kg,
            before?.anthropometry_anamnesis?.height_cm,
          )
        : before?.anthropometry_anamnesis?.[key];
    let toRaw =
      key === "bmi"
        ? calcBmi(
            after?.anthropometry_anamnesis?.weight_kg,
            after?.anthropometry_anamnesis?.height_cm,
          )
        : after?.anthropometry_anamnesis?.[key];
    if (key === "body_type") {
      fromRaw = ANTHROPOMETRY_BODY_TYPE_LABELS[fromRaw] || fromRaw;
      toRaw = ANTHROPOMETRY_BODY_TYPE_LABELS[toRaw] || toRaw;
    }
    if (key === "volume_redistribution") {
      fromRaw = ANTHROPOMETRY_REDISTRIBUTION_LABELS[fromRaw] || fromRaw;
      toRaw = ANTHROPOMETRY_REDISTRIBUTION_LABELS[toRaw] || toRaw;
    }
    const fromValue = normalizeComparableValue(fromRaw);
    const toValue = normalizeComparableValue(toRaw);
    if (fromValue !== toValue) {
      changes.push({
        label: ANTHROPOMETRY_FIELD_LABELS[key],
        from: fromRaw,
        to: toRaw,
      });
    }
  });

  return changes;
};

const humanizeFieldKey = (key) => {
  if (!key) return "—";
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return String(key)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
};

const formatAnyValue = (value) => {
  if (value == null || value === "") return "—";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  if (Array.isArray(value)) {
    const parts = value
      .map((v) =>
        v && typeof v === "object"
          ? (v?.name ?? v?.title ?? JSON.stringify(v))
          : String(v),
      )
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  }
  if (typeof value === "object") {
    if ("name" in value && value?.name != null) return String(value.name);
    if ("title" in value && value?.title != null) return String(value.title);
    if ("value" in value && value?.value != null)
      return formatAnyValue(value.value);
    return JSON.stringify(value);
  }
  return String(value);
};

// Вспомогательная функция для безопасного преобразования данных пациента в форму
const mapPatientToFormData = (patient) => {
  const fields = patient?.fields || {};
  const docValue = patient?.doc_name?.value;

  const doc_id =
    docValue && typeof docValue === "object" && docValue.id
      ? String(docValue.id)
      : "";

  return {
    fio: patient?.fio?.value ?? "",
    course: patient?.course?.value ?? "",
    cart: patient?.cart?.value ?? "",
    doc_id,
    biological_age: fields?.biological_age?.value ?? "",
    cardiovascular_risk: fields?.cardiovascular_risk?.value ?? "",
    chest_circumference_cm: fields?.chest_circumference_cm?.value ?? "",
    waist_circumference_cm: fields?.waist_circumference_cm?.value ?? "",
    hips_circumference_cm: fields?.hips_circumference_cm?.value ?? "",
    min_weight_kg: fields?.min_weight_kg?.value ?? "",
    current_weight_kg: fields?.current_weight_kg?.value ?? "",
    comfortable_weight_kg: fields?.comfortable_weight_kg?.value ?? "",
    blood_tests: fields?.blood_tests?.value ?? "",
    blood_pressure: fields?.blood_pressure?.value ?? "",
    medical_summary: fields?.medical_summary?.value ?? "",
    allergies: fields?.allergies?.value ?? "",
    diagnosis: fields?.diagnosis?.value ?? "",
    complaints: fields?.complaints?.value ?? "",
    life_anamnesis: parseLifeAnamnesis(fields?.life_anamnesis?.value),
    somatic_diseases: parseSomaticDiseases(fields?.somatic_diseases?.value),
    gyn_anamnesis: parseGynAnamnesis(fields?.gyn_anamnesis?.value),
    allergy_anamnesis: parseAllergyAnamnesis(fields?.allergy_anamnesis?.value),
    nutrition_anamnesis: parseNutritionAnamnesis(
      fields?.nutrition_anamnesis?.value,
    ),
    daily_routine_anamnesis: parseDailyRoutineAnamnesis(
      fields?.daily_routine_anamnesis?.value,
    ),
    anthropometry_anamnesis: parseAnthropometryAnamnesis(
      fields?.anthropometry_anamnesis?.value,
    ),
    epid_anamnesis: parseEpidAnamnesis(fields?.epid_anamnesis?.value),
    permanent_therapy: fields?.permanent_therapy?.value ?? "",
    doctor_comment: fields?.doctor_comment?.value ?? "",
    cardiovascular_diseases: fields?.cardiovascular_diseases?.value ?? "",
    cns_diseases: fields?.cns_diseases?.value ?? "",
    respiratory_diseases: fields?.respiratory_diseases?.value ?? "",
    digestive_diseases: fields?.digestive_diseases?.value ?? "",
    vision_impairments: fields?.vision_impairments?.value ?? "",
    past_conditions: fields?.past_conditions?.value ?? "",
    heredity: fields?.heredity?.value ?? "",
    gynecological_history: fields?.gynecological_history?.value ?? "",
    thyroid_disease: fields?.thyroid_disease?.value ?? "",
    bad_habits: fields?.bad_habits?.value ?? "",
    operations: fields?.operations?.value ?? "",
    injuries: fields?.injuries?.value ?? "",
  };
};

const PatientProfile = () => {
  const { t } = useTranslation("patient");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(mapPatientToFormData(null));

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [showAiModal, setShowAiModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    getPatients(patientId);
  }, [patientId]);

  const getPatients = async (id) => {
    try {
      const data = await GetData({
        module: "patient",
        block: "info",
        id_patient: id,
      });
      const p = data.result;
      console.log("P:", data);
      setPatient(p);
      setFormData(mapPatientToFormData(p));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  console.log("Patient:", patient);

  const handleEdit = () => setIsEditing(true);

  const handlePrint = () => {
    navigate(`/patient/${patientId}/profile/print`);
  };

  const handleBack = () => {
    navigate(`/patient/${patientId}/profile`);
  };

  const handleAiAnalysis = async () => {
    setShowAiModal(true);
    setAiLoading(true);
    setAiResult(null);
    setAiError(null);
    try {
      const data = await GetData({
        module: "patient",
        block: "ai_profile_indicators",
        id_patient: patientId,
      });
      if (data?.response !== undefined || data?.isAxiosError) {
        setAiError(
          data?.message || data?.response?.data?.message || t("anamnesis.ai.requestError"),
        );
        return;
      }
      if (data?.message) {
        setAiError(data.message);
        return;
      }
      const biologicalAge = data?.result?.biological_age;
      const cardiovascularRisk = data?.result?.cardiovascular_risk;
      if (biologicalAge == null || cardiovascularRisk == null) {
        setAiError(t("anamnesis.ai.noAnswer"));
        return;
      }

      setPatient((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fields: {
            ...prev.fields,
            biological_age: {
              ...(prev.fields?.biological_age ?? { access: "change" }),
              value: biologicalAge,
            },
            cardiovascular_risk: {
              ...(prev.fields?.cardiovascular_risk ?? { access: "change" }),
              value: cardiovascularRisk,
            },
          },
        };
      });
      setFormData((prev) => ({
        ...prev,
        biological_age: biologicalAge,
        cardiovascular_risk: cardiovascularRisk,
      }));
      setAiResult(
        `${t("anamnesis.ai.bioAge")}: ${biologicalAge}\n${t("anamnesis.ai.cvRisk")}: ${cardiovascularRisk}%`,
      );
    } catch (err) {
      setAiError(err?.message || t("anamnesis.ai.requestError"));
    } finally {
      setAiLoading(false);
    }
  };

  const handleCloseAiModal = () => {
    setShowAiModal(false);
    setAiResult(null);
    setAiError(null);
  };

  const handleCancel = () => {
    setFormData(mapPatientToFormData(patient));
    setIsEditing(false);
  };

  const handleSave = async () => {
    const {
      fio,
      cart,
      course,
      doc_id,
      life_anamnesis,
      somatic_diseases,
      gyn_anamnesis,
      allergy_anamnesis,
      nutrition_anamnesis,
      daily_routine_anamnesis,
      anthropometry_anamnesis,
      epid_anamnesis,
      ...restFields
    } = formData;
    const previousFormData = mapPatientToFormData(patient);
    const fieldsData = {
      ...restFields,
      life_anamnesis: serializeLifeAnamnesis(life_anamnesis),
      somatic_diseases: serializeSomaticDiseases(somatic_diseases),
      gyn_anamnesis: serializeGynAnamnesis(gyn_anamnesis),
      allergy_anamnesis: serializeAllergyAnamnesis(allergy_anamnesis),
      nutrition_anamnesis: serializeNutritionAnamnesis(nutrition_anamnesis),
      daily_routine_anamnesis: serializeDailyRoutineAnamnesis(
        daily_routine_anamnesis,
      ),
      anthropometry_anamnesis: serializeAnthropometryAnamnesis(
        anthropometry_anamnesis,
      ),
      epid_anamnesis: serializeEpidAnamnesis(epid_anamnesis),
    };

    try {
      const result = await GetData({
        module: "patient",
        block: "info",
        id_patient: patientId,
        action: "change",
        fio,
        cart,
        course,
        id_user: doc_id || null,
        fields: fieldsData,
      });

      if (result && result.result) {
        const changes = collectChangedFields(
          previousFormData,
          formData,
          PROFILE_FIELD_LABELS,
        );
        await logPatientChange({
          idPatient: patientId,
          text: buildPatientLogText("patient_profile_updated", {
            changes,
          }),
        });
        await getPatients(patientId);
        setIsEditing(false);
        showAlert(t("anamnesis.errors.saved"));
      } else {
        throw new Error(result?.message || t("anamnesis.errors.badResponse"));
      }
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      setError(t("anamnesis.errors.errorPrefix", { message: err.message }));
      showAlert(t("anamnesis.errors.saveFailedWith", { message: err.message }));
    }
  };

  // Функции для работы с модальным окна уведомлений
  const showAlert = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLifeAnamnesisChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      life_anamnesis: {
        ...prev.life_anamnesis,
        [name]: value,
      },
    }));
  };

  const handleSomaticDiseasesChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      somatic_diseases: {
        ...prev.somatic_diseases,
        [name]: value,
      },
    }));
  };

  const handleGynAnamnesisChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      gyn_anamnesis: {
        ...prev.gyn_anamnesis,
        [name]: value,
      },
    }));
  };

  const handleGynRegularityChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      gyn_anamnesis: {
        ...prev.gyn_anamnesis,
        regularity: prev.gyn_anamnesis?.regularity === value ? "" : value,
      },
    }));
  };

  const handleYesNoAnamnesisAnswerChange = (blockKey, fieldKey, answer) => {
    setFormData((prev) => {
      const current = prev[blockKey]?.[fieldKey] || {
        ...YES_NO_ANAMNESIS_ITEM_DEFAULT,
      };
      return {
        ...prev,
        [blockKey]: {
          ...prev[blockKey],
          [fieldKey]: {
            ...current,
            answer: current.answer === answer ? "" : answer,
          },
        },
      };
    });
  };

  const handleYesNoAnamnesisNoteChange = (blockKey, fieldKey, note) => {
    setFormData((prev) => ({
      ...prev,
      [blockKey]: {
        ...prev[blockKey],
        [fieldKey]: {
          ...(prev[blockKey]?.[fieldKey] || {
            ...YES_NO_ANAMNESIS_ITEM_DEFAULT,
          }),
          note,
        },
      },
    }));
  };

  const handleNutritionAnamnesisChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      nutrition_anamnesis: {
        ...prev.nutrition_anamnesis,
        [name]: value,
      },
    }));
  };

  const handleNutritionMealRegimeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      nutrition_anamnesis: {
        ...prev.nutrition_anamnesis,
        meal_regime:
          prev.nutrition_anamnesis?.meal_regime === value ? "" : value,
      },
    }));
  };

  const handleNutritionFoodCharacterToggle = (key) => {
    setFormData((prev) => {
      const current = normalizeNutritionFoodCharacter(
        prev.nutrition_anamnesis?.food_character,
      );
      const next = current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key];
      return {
        ...prev,
        nutrition_anamnesis: {
          ...prev.nutrition_anamnesis,
          food_character: next,
        },
      };
    });
  };

  const handleDailyRoutineChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      daily_routine_anamnesis: {
        ...prev.daily_routine_anamnesis,
        [name]: value,
      },
    }));
  };

  const handleDailyRoutineSingleToggle = (fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      daily_routine_anamnesis: {
        ...prev.daily_routine_anamnesis,
        [fieldKey]:
          prev.daily_routine_anamnesis?.[fieldKey] === value ? "" : value,
      },
    }));
  };

  const handleDailyRoutineMultiToggle = (fieldKey, allowedKeys, value) => {
    setFormData((prev) => {
      const current = normalizeCheckboxKeys(
        prev.daily_routine_anamnesis?.[fieldKey],
        allowedKeys,
      );
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return {
        ...prev,
        daily_routine_anamnesis: {
          ...prev.daily_routine_anamnesis,
          [fieldKey]: next,
        },
      };
    });
  };

  const handleAnthropometryChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      anthropometry_anamnesis: {
        ...prev.anthropometry_anamnesis,
        [name]: value,
      },
    }));
  };

  const handleAnthropometrySingleToggle = (fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      anthropometry_anamnesis: {
        ...prev.anthropometry_anamnesis,
        [fieldKey]:
          prev.anthropometry_anamnesis?.[fieldKey] === value ? "" : value,
      },
    }));
  };

  const displayValue = (value) => (!value && value !== 0 ? "—" : value);
  const displayLinedValue = (value) => (!value && value !== 0 ? "" : value);
  const displayCourseValue = (value) => {
    if (value == null) return "—";
    const s = String(value).trim();
    return !s || s === "0" ? "—" : s;
  };

  if (loading) return <div>{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div>{t("error", { ns: "common", message: error })}</div>;
  if (!patient) return <div>{t("anamnesis.errors.patientNotFound")}</div>;

  const f = formData;
  const p = patient;

  const allFieldsEntries = Object.entries(p?.fields || {});
  const knownKeys = new Set(Object.keys(FIELD_LABELS));
  const sortedFieldsEntries = [
    ...allFieldsEntries.filter(([k]) => knownKeys.has(k)),
    ...allFieldsEntries
      .filter(([k]) => !knownKeys.has(k))
      .sort(([a], [b]) => String(a).localeCompare(String(b), "ru")),
  ];

  return (
    <>
      <div className="patient-name-title-sticky">
        <BlockTitle
          title={t("profile.title")}
          buttons={
            isEditing
              ? [
                  {
                    label: t("docs.buttons.save"),
                    onClick: handleSave,
                    type: "normal",
                  },
                  {
                    label: t("docs.buttons.cancel"),
                    onClick: handleCancel,
                    type: "light",
                  },
                ]
              : [
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ fill: "currentColor" }}>
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                        </svg>
                        {t("docs.buttons.edit")}
                      </span>
                    ),
                    onClick: handleEdit,
                    type: "light icon",
                  },
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{
                            fill: "currentColor",
                            transform: "scale(1.2)",
                            transformOrigin: "center",
                          }}>
                          <path d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z" />
                        </svg>
                        {t("docs.buttons.print")}
                      </span>
                    ),
                    onClick: handlePrint,
                    type: "light icon",
                  },
                  {
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}>
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          width="20"
                          height="20"
                          style={{ color: "currentColor" }}>
                          <path
                            d="M9 14L4 9l5-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4 9h11a4 4 0 1 1 0 8H9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {t("docs.buttons.backToList")}
                      </span>
                    ),
                    onClick: handleBack,
                    type: "normal icon",
                  },
                  // {
                  //   label: "ИИ Анализ",
                  //   onClick: handleAiAnalysis,
                  //   type: "light",
                  // },
                ]
          }
        />
      </div>

      <div className="patient-profile-print mt-4">
        {(() => {
          const todayIso = new Date().toISOString().slice(0, 10);
          const visitDate = formatDateForDisplay(todayIso);
          const fioPatient = formatAnyValue(p.fio?.value);
          const fioDoctor = formatAnyValue(p.doc_name?.value?.name || "");
          const cardNumber = formatAnyValue(p.cart?.value);
          const birthDate = formatAnyValue(p.birthDate?.value);
          const ageYears = calcAgeYears(p.birthDate?.value);
          const ageText = ageYears == null ? "—" : String(ageYears);

          const groups = [
            {
              title: t("profile.groups.analyses"),
              keys: ["blood_tests", "blood_pressure"],
            },
            {
              title: t("profile.groups.volumes"),
              keys: [
                "chest_circumference_cm",
                "waist_circumference_cm",
                "hips_circumference_cm",
              ],
            },
            { title: t("profile.groups.allergies"), keys: ["allergies"] },
            {
              title: t("profile.groups.diseaseAnamnesis"),
              keys: [
                "complaints",
                "medical_summary",
                "diagnosis",
                "permanent_therapy",
              ],
            },
            {
              title: t("profile.groups.lifeAnamnesis"),
              keys: [
                "cardiovascular_diseases",
                "cns_diseases",
                "respiratory_diseases",
                "digestive_diseases",
                "vision_impairments",
                "past_conditions",
                "heredity",
                "gynecological_history",
                "thyroid_disease",
                "bad_habits",
                "operations",
                "injuries",
              ],
            },
          ];

          const usedKeys = new Set(groups.flatMap((g) => g.keys));
          const otherKeys = sortedFieldsEntries
            .map(([k]) => k)
            .filter(
              (k) =>
                !usedKeys.has(k) &&
                k !== "life_anamnesis" &&
                k !== "somatic_diseases" &&
                k !== "gyn_anamnesis" &&
                k !== "allergy_anamnesis" &&
                k !== "nutrition_anamnesis" &&
                k !== "daily_routine_anamnesis" &&
                k !== "anthropometry_anamnesis" &&
                k !== "epid_anamnesis",
            );
          if (otherKeys.length) {
            groups.push({ title: t("profile.groups.other"), keys: otherKeys });
          }

          return (
            <>
              <style>{`
                @media print {
                  @page {
                    size: A4 portrait;
                    margin: 12mm;
                  }

                  .patient-profile-print table,
                  .patient-profile-print thead,
                  .patient-profile-print tbody,
                  .patient-profile-print tfoot,
                  .patient-profile-print tr,
                  .patient-profile-print th,
                  .patient-profile-print td {
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                  }
                }
              `}</style>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                }}>
                <img
                  src={LogoText}
                  alt={t("profile.print.logoAlt")}
                  style={{ width: 140, height: 56, objectFit: "contain" }}
                />
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}>
                  <div>{t("profile.print.medCard")}</div>
                  <div>{t("profile.print.outpatient")}</div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0,
                  marginBottom: 12,
                }}>
                <div style={{ padding: 0 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "45% 1fr",
                      padding: "4px 6px",
                    }}>
                    <div style={{ fontWeight: 600 }}>{t("profile.print.visitDate")}</div>
                    <div>{visitDate}</div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "45% 1fr",
                      padding: "4px 6px",
                    }}>
                    <div style={{ fontWeight: 600 }}>{t("profile.print.patientFio")}</div>
                    <div>{fioPatient}</div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "45% 1fr",
                      padding: "4px 6px",
                    }}>
                    <div style={{ fontWeight: 600 }}>{t("profile.print.doctorFio")}</div>
                    <div>{fioDoctor}</div>
                  </div>
                </div>

                <div style={{ padding: 0 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "45% 1fr",
                      padding: "4px 6px",
                    }}>
                    <div style={{ fontWeight: 600 }}>{t("profile.print.cardNumber")}</div>
                    <div>{cardNumber}</div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "45% 1fr",
                      padding: "4px 6px",
                    }}>
                    <div style={{ fontWeight: 600 }}>{t("profile.print.birthDate")}</div>
                    <div>
                      {typeof p.birthDate?.value === "string"
                        ? formatDateForDisplay(p.birthDate.value)
                        : birthDate}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "45% 1fr",
                      padding: "4px 6px",
                    }}>
                    <div style={{ fontWeight: 600 }}>{t("profile.print.age")}</div>
                    <div>{ageText}</div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  textAlign: "center",
                  fontWeight: 800,
                  fontSize: 18,
                  textTransform: "uppercase",
                  margin: "24px 0 10px",
                }}>
                {t("profile.print.protocol")}
              </div>

              <p style={{ margin: "0 0 10px 0" }}>
                <strong>{t("profile.fields.diagnosis")}:</strong>{" "}
                {formatAnyValue(p?.fields?.diagnosis?.value)}
              </p>

              {groups.map((g) => (
                <div key={g.title} style={{ margin: "0 0 12px 0" }}>
                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: 800,
                      fontSize: 16,
                      marginBottom: 4,
                    }}>
                    {g.title}
                  </div>
                  <div>
                    {g.keys
                      .map((key) => {
                        const raw = p?.fields?.[key]?.value;
                        const valueText = formatAnyValue(raw);
                        return { key, text: valueText };
                      })
                      .map(({ key, text }, idx, arr) => (
                        <span key={key}>
                          <strong>{humanizeFieldKeyLocalized(key, t)}:</strong> {text}
                          {idx < arr.length - 1 ? "; " : ""}
                        </span>
                      ))}
                  </div>
                </div>
              ))}

              <div className="patient-profile-print-consent">
                <p className="patient-profile-print-consent-text">
                  {t("profile.print.consentLead")}{" "}
                  <span className="patient-profile-print-consent-fio-line" />
                  <br />
                  {t("profile.print.consentBody")}
                </p>
                <div className="patient-profile-print-consent-signs">
                  <div className="patient-profile-print-consent-sign">
                    <span className="patient-profile-print-consent-sign-line" />
                    <span className="patient-profile-print-consent-sign-label">
                      {t("profile.print.patientSignature")}
                    </span>
                  </div>
                  <div className="patient-profile-print-consent-sign">
                    <span className="patient-profile-print-consent-sign-line patient-profile-print-consent-sign-line--wide" />
                    <span className="patient-profile-print-consent-sign-label">
                      {t("profile.print.signatureDecoding")}
                    </span>
                  </div>
                  <div className="patient-profile-print-consent-sign">
                    <span className="patient-profile-print-consent-date">
                      {t("profile.print.datePlaceholder")}
                    </span>
                    <span className="patient-profile-print-consent-sign-label">
                      {t("profile.print.dateLabel")}
                    </span>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      <Form
        className={`patient-profile mt-4${isEditing ? " patient-profile--editing" : ""}`}>
        {/* Анамнез жизни (новый блок) */}
        <div className="patient-life-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">{t("anamnesis.sections.life")}</div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="life-complaints">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.labels.generalComplaints")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="complaints"
                        value={f.complaints}
                        onChange={handleChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(p.fields?.complaints?.value)}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          {Object.keys(LIFE_ANAMNESIS_FIELD_LABELS)
            .filter((fieldKey) => fieldKey !== "medications")
            .map((fieldKey) => (
              <Row className="mb-3" key={fieldKey}>
                <Col md={12}>
                  <div className="patient-form-group">
                    <Form.Group controlId={`life-${fieldKey}`}>
                      <div className="patient-inline-field">
                        <Form.Label className="patient-inline-label">
                          {t(LIFE_ANAMNESIS_FIELD_KEYS[fieldKey])}:
                        </Form.Label>
                        {isEditing ? (
                          <Form.Control
                            className="patient-inline-value"
                            name={fieldKey}
                            value={f.life_anamnesis?.[fieldKey] ?? ""}
                            onChange={handleLifeAnamnesisChange}
                            placeholder=" "
                          />
                        ) : (
                          <div className="patient-inline-value">
                            {displayLinedValue(
                              parseLifeAnamnesis(
                                p.fields?.life_anamnesis?.value,
                              )?.[fieldKey],
                            )}
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </div>
                </Col>
              </Row>
            ))}

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="life-medications">
                  <div className="patient-inline-field patient-inline-field--medications">
                    <Form.Label className="patient-inline-label">
                      {t(LIFE_ANAMNESIS_FIELD_KEYS.medications)}:
                    </Form.Label>
                    <div className="patient-medications-field">
                      {isEditing ? (
                        <Form.Control
                          as="textarea"
                          rows={4}
                          className="patient-inline-value patient-medications-input"
                          name="medications"
                          value={f.life_anamnesis?.medications ?? ""}
                          onChange={handleLifeAnamnesisChange}
                          placeholder=" "
                        />
                      ) : (
                        <div className="patient-inline-value patient-medications-value">
                          {displayLinedValue(
                            parseLifeAnamnesis(p.fields?.life_anamnesis?.value)
                              ?.medications,
                          )}
                        </div>
                      )}
                      <div className="patient-medications-hint">
                        {t("anamnesis.life.medicationsHint")}
                      </div>
                    </div>
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        {/* Общие соматические заболевания */}
        <div className="patient-life-anamnesis-block patient-somatic-diseases-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.somatic")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="somatic-chronic-diseases">
                  <Form.Label className="patient-somatic-field-label">
                    {t(SOMATIC_DISEASES_FIELD_KEYS.chronic_diseases)}:
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className="patient-lined-input patient-lined-input--3"
                      name="chronic_diseases"
                      value={f.somatic_diseases?.chronic_diseases ?? ""}
                      onChange={handleSomaticDiseasesChange}
                      placeholder=" "
                    />
                  ) : (
                    <div className="patient-lined-value patient-lined-value--3">
                      {displayLinedValue(
                        parseSomaticDiseases(p.fields?.somatic_diseases?.value)
                          ?.chronic_diseases,
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        {/* Эндокринный статус */}
        <div className="patient-life-anamnesis-block patient-somatic-diseases-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.endocrine")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="somatic-endocrine-status">
                  {isEditing ? (
                    <Form.Control
                      className="patient-inline-value"
                      name="endocrine_status"
                      value={f.somatic_diseases?.endocrine_status ?? ""}
                      onChange={handleSomaticDiseasesChange}
                      placeholder=" "
                    />
                  ) : (
                    <div className="patient-inline-value">
                      {displayLinedValue(
                        parseSomaticDiseases(p.fields?.somatic_diseases?.value)
                          ?.endocrine_status,
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        {/* Гинекологический анамнез */}
        <div className="patient-life-anamnesis-block patient-gyn-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.gyn")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-gyn-inline-row">
                  <span>{t("anamnesis.gyn.menstruationFrom")}</span>
                  {isEditing ? (
                    <Form.Control
                      className="patient-gyn-short-input"
                      name="menstruation_age"
                      value={f.gyn_anamnesis?.menstruation_age ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <span className="patient-gyn-short-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.menstruation_age,
                      )}
                    </span>
                  )}
                  <span>{t("anamnesis.gyn.yearsShort")}</span>
                  <label className="patient-gyn-check">
                    <input
                      type="checkbox"
                      checked={
                        (isEditing
                          ? f.gyn_anamnesis?.regularity
                          : parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                              ?.regularity) === "regular"
                      }
                      onChange={() => handleGynRegularityChange("regular")}
                    />
                    {t("anamnesis.gyn.regular")}
                  </label>
                  <label className="patient-gyn-check">
                    <input
                      type="checkbox"
                      checked={
                        (isEditing
                          ? f.gyn_anamnesis?.regularity
                          : parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                              ?.regularity) === "irregular"
                      }
                      onChange={() => handleGynRegularityChange("irregular")}
                    />
                    {t("anamnesis.gyn.irregular")}
                  </label>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="gyn-cycle-duration">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.gyn.cycleDuration")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="cycle_duration"
                        value={f.gyn_anamnesis?.cycle_duration ?? ""}
                        onChange={handleGynAnamnesisChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                            ?.cycle_duration,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="gyn-pms">
                  <Form.Label className="patient-gyn-field-label">
                    {t("anamnesis.gyn.pms")}:
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="patient-lined-input patient-lined-input--2"
                      name="pms"
                      value={f.gyn_anamnesis?.pms ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <div className="patient-lined-value patient-lined-value--2">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)?.pms,
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-gyn-inline-row patient-gyn-pregnancy-row">
                  <span>{t("anamnesis.gyn.pregnancyTotal")}</span>
                  {isEditing ? (
                    <Form.Control
                      className="patient-gyn-tiny-input"
                      name="pregnancy_r"
                      value={f.gyn_anamnesis?.pregnancy_r ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <span className="patient-gyn-tiny-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.pregnancy_r,
                      )}
                    </span>
                  )}
                  <span>{t("anamnesis.gyn.pregnancyA")}</span>
                  {isEditing ? (
                    <Form.Control
                      className="patient-gyn-tiny-input"
                      name="pregnancy_a"
                      value={f.gyn_anamnesis?.pregnancy_a ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <span className="patient-gyn-tiny-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.pregnancy_a,
                      )}
                    </span>
                  )}
                  <span>{t("anamnesis.gyn.pregnancyV")}</span>
                  {isEditing ? (
                    <Form.Control
                      className="patient-gyn-tiny-input"
                      name="pregnancy_v"
                      value={f.gyn_anamnesis?.pregnancy_v ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <span className="patient-gyn-tiny-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.pregnancy_v,
                      )}
                    </span>
                  )}
                  <span>)</span>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="gyn-menstrual-symptoms">
                  <Form.Label className="patient-gyn-field-label">
                    {t("anamnesis.gyn.menstrualSymptoms")}:
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      className="patient-inline-value"
                      name="menstrual_symptoms"
                      value={f.gyn_anamnesis?.menstrual_symptoms ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <div className="patient-inline-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.menstrual_symptoms,
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-gyn-inline-row">
                  <span>{t("anamnesis.gyn.menopauseFrom")}</span>
                  {isEditing ? (
                    <Form.Control
                      className="patient-gyn-short-input"
                      name="menopause_age"
                      value={f.gyn_anamnesis?.menopause_age ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <span className="patient-gyn-short-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.menopause_age,
                      )}
                    </span>
                  )}
                  <span>{t("anamnesis.gyn.yearsParen")}</span>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="gyn-hormonal-drugs">
                  <Form.Label className="patient-gyn-field-label">
                    {t("anamnesis.gyn.hormonalDrugs")}:
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      className="patient-inline-value"
                      name="hormonal_drugs"
                      value={f.gyn_anamnesis?.hormonal_drugs ?? ""}
                      onChange={handleGynAnamnesisChange}
                      placeholder=" "
                    />
                  ) : (
                    <div className="patient-inline-value">
                      {displayLinedValue(
                        parseGynAnamnesis(p.fields?.gyn_anamnesis?.value)
                          ?.hormonal_drugs,
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="gyn-diseases">
                  <Form.Label className="patient-gyn-field-label">
                    {t("anamnesis.gyn.diseases")}:
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className="patient-lined-input patient-lined-input--3"
                      name="gynecological_history"
                      value={f.gynecological_history}
                      onChange={handleChange}
                      placeholder=" "
                    />
                  ) : (
                    <div className="patient-lined-value patient-lined-value--3">
                      {displayLinedValue(
                        p.fields?.gynecological_history?.value,
                      )}
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        {/* Аллергический анамнез */}
        <div className="patient-life-anamnesis-block patient-yesno-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.allergy")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-yesno-table">
                  <div className="patient-yesno-header">
                    <span className="patient-yesno-label-col" />
                    <span className="patient-yesno-check-col">{t("anamnesis.labels.no")}</span>
                    <span className="patient-yesno-check-col">{t("anamnesis.labels.yes")}</span>
                    <span className="patient-yesno-note-col" />
                  </div>
                  {ALLERGY_ANAMNESIS_KEYS.map((fieldKey) => {
                    const item = isEditing
                      ? f.allergy_anamnesis?.[fieldKey]
                      : parseAllergyAnamnesis(
                          p.fields?.allergy_anamnesis?.value,
                        )?.[fieldKey];
                    return (
                      <div className="patient-yesno-row" key={fieldKey}>
                        <span className="patient-yesno-label-col">
                          {t(ALLERGY_ANAMNESIS_FIELD_KEYS[fieldKey])}
                        </span>
                        <label className="patient-yesno-check-col patient-yesno-check">
                          <input
                            type="checkbox"
                            checked={item?.answer === "no"}
                            onChange={() =>
                              handleYesNoAnamnesisAnswerChange(
                                "allergy_anamnesis",
                                fieldKey,
                                "no",
                              )
                            }
                          />
                        </label>
                        <label className="patient-yesno-check-col patient-yesno-check">
                          <input
                            type="checkbox"
                            checked={item?.answer === "yes"}
                            onChange={() =>
                              handleYesNoAnamnesisAnswerChange(
                                "allergy_anamnesis",
                                fieldKey,
                                "yes",
                              )
                            }
                          />
                        </label>
                        {isEditing ? (
                          <Form.Control
                            className="patient-yesno-note-col patient-yesno-note-input"
                            value={item?.note ?? ""}
                            onChange={(e) =>
                              handleYesNoAnamnesisNoteChange(
                                "allergy_anamnesis",
                                fieldKey,
                                e.target.value,
                              )
                            }
                            placeholder=" "
                          />
                        ) : (
                          <span className="patient-yesno-note-col patient-yesno-note-value">
                            {displayLinedValue(item?.note)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Эпидемиологический анамнез */}
        <div className="patient-life-anamnesis-block patient-yesno-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.epid")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-yesno-table">
                  <div className="patient-yesno-header">
                    <span className="patient-yesno-label-col" />
                    <span className="patient-yesno-check-col">{t("anamnesis.labels.no")}</span>
                    <span className="patient-yesno-check-col">{t("anamnesis.labels.yes")}</span>
                    <span className="patient-yesno-note-col" />
                  </div>
                  {EPID_ANAMNESIS_KEYS.map((fieldKey) => {
                    const item = isEditing
                      ? f.epid_anamnesis?.[fieldKey]
                      : parseEpidAnamnesis(p.fields?.epid_anamnesis?.value)?.[
                          fieldKey
                        ];
                    return (
                      <div className="patient-yesno-row" key={fieldKey}>
                        <span className="patient-yesno-label-col">
                          {t(EPID_ANAMNESIS_FIELD_KEYS[fieldKey])}
                        </span>
                        <label className="patient-yesno-check-col patient-yesno-check">
                          <input
                            type="checkbox"
                            checked={item?.answer === "no"}
                            onChange={() =>
                              handleYesNoAnamnesisAnswerChange(
                                "epid_anamnesis",
                                fieldKey,
                                "no",
                              )
                            }
                          />
                        </label>
                        <label className="patient-yesno-check-col patient-yesno-check">
                          <input
                            type="checkbox"
                            checked={item?.answer === "yes"}
                            onChange={() =>
                              handleYesNoAnamnesisAnswerChange(
                                "epid_anamnesis",
                                fieldKey,
                                "yes",
                              )
                            }
                          />
                        </label>
                        {isEditing ? (
                          <Form.Control
                            className="patient-yesno-note-col patient-yesno-note-input"
                            value={item?.note ?? ""}
                            onChange={(e) =>
                              handleYesNoAnamnesisNoteChange(
                                "epid_anamnesis",
                                fieldKey,
                                e.target.value,
                              )
                            }
                            placeholder=" "
                          />
                        ) : (
                          <span className="patient-yesno-note-col patient-yesno-note-value">
                            {displayLinedValue(item?.note)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Характер питания */}
        <div className="patient-life-anamnesis-block patient-nutrition-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.nutrition")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.nutrition.mealRegime")}:
                  </span>
                  <div className="patient-nutrition-checks patient-nutrition-checks--stacked">
                    <label className="patient-nutrition-check">
                      <input
                        type="checkbox"
                        checked={
                          (isEditing
                            ? f.nutrition_anamnesis?.meal_regime
                            : parseNutritionAnamnesis(
                                p.fields?.nutrition_anamnesis?.value,
                              )?.meal_regime) === "regular"
                        }
                        onChange={() =>
                          handleNutritionMealRegimeChange("regular")
                        }
                      />
                      {t("anamnesis.nutrition.regular")}
                    </label>
                    <label className="patient-nutrition-check">
                      <input
                        type="checkbox"
                        checked={
                          (isEditing
                            ? f.nutrition_anamnesis?.meal_regime
                            : parseNutritionAnamnesis(
                                p.fields?.nutrition_anamnesis?.value,
                              )?.meal_regime) === "irregular"
                        }
                        onChange={() =>
                          handleNutritionMealRegimeChange("irregular")
                        }
                      />
                      {t("anamnesis.nutrition.irregular")}
                    </label>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="nutrition-meals-per-day">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.nutrition.mealsPerDay")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="meals_per_day"
                        value={f.nutrition_anamnesis?.meals_per_day ?? ""}
                        onChange={handleNutritionAnamnesisChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseNutritionAnamnesis(
                            p.fields?.nutrition_anamnesis?.value,
                          )?.meals_per_day,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-food-character-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.nutrition.foodCharacter")}:
                  </span>
                  <div className="patient-nutrition-checks patient-nutrition-checks--two-cols">
                    {(() => {
                      const selected = isEditing
                        ? normalizeNutritionFoodCharacter(
                            f.nutrition_anamnesis?.food_character,
                          )
                        : normalizeNutritionFoodCharacter(
                            parseNutritionAnamnesis(
                              p.fields?.nutrition_anamnesis?.value,
                            )?.food_character,
                          );
                      return NUTRITION_FOOD_CHARACTER_KEYS.map((key) => (
                        <label className="patient-nutrition-check" key={key}>
                          <input
                            type="checkbox"
                            checked={selected.includes(key)}
                            onChange={() =>
                              handleNutritionFoodCharacterToggle(key)
                            }
                          />
                          {t(NUTRITION_FOOD_CHARACTER_LABEL_KEYS[key])}
                        </label>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="nutrition-fluid-liters">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.nutrition.fluidLiters")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="fluid_liters"
                        value={f.nutrition_anamnesis?.fluid_liters ?? ""}
                        onChange={handleNutritionAnamnesisChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseNutritionAnamnesis(
                            p.fields?.nutrition_anamnesis?.value,
                          )?.fluid_liters,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-habits-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.nutrition.smokingAlcohol")}:
                  </span>
                  {isEditing ? (
                    <Form.Control
                      className="patient-nutrition-habit-input"
                      name="smoking_alcohol"
                      value={f.nutrition_anamnesis?.smoking_alcohol ?? ""}
                      onChange={handleNutritionAnamnesisChange}
                      placeholder={t("anamnesis.labels.quantityPlaceholder")}
                    />
                  ) : (
                    <span className="patient-nutrition-habit-value">
                      {displayLinedValue(
                        parseNutritionAnamnesis(
                          p.fields?.nutrition_anamnesis?.value,
                        )?.smoking_alcohol,
                      )}
                    </span>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Распорядок дня */}
        <div className="patient-life-anamnesis-block patient-nutrition-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.routine")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="daily-routine-sleep-hours">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.routine.sleepHours")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="sleep_hours"
                        value={f.daily_routine_anamnesis?.sleep_hours ?? ""}
                        onChange={handleDailyRoutineChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseDailyRoutineAnamnesis(
                            p.fields?.daily_routine_anamnesis?.value,
                          )?.sleep_hours,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.routine.sleepCharacter")}:
                  </span>
                  <div className="patient-nutrition-checks patient-nutrition-checks--stacked">
                    {(() => {
                      const selected = normalizeCheckboxKeys(
                        isEditing
                          ? f.daily_routine_anamnesis?.sleep_character
                          : parseDailyRoutineAnamnesis(
                              p.fields?.daily_routine_anamnesis?.value,
                            )?.sleep_character,
                        DAILY_ROUTINE_SLEEP_CHARACTER_KEYS,
                      );
                      return DAILY_ROUTINE_SLEEP_CHARACTER_KEYS.map((key) => (
                        <label className="patient-nutrition-check" key={key}>
                          <input
                            type="checkbox"
                            checked={selected.includes(key)}
                            onChange={() =>
                              handleDailyRoutineMultiToggle(
                                "sleep_character",
                                DAILY_ROUTINE_SLEEP_CHARACTER_KEYS,
                                key,
                              )
                            }
                          />
                          {t(DAILY_ROUTINE_SLEEP_CHARACTER_LABEL_KEYS[key])}
                        </label>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.routine.awakening")}:
                  </span>
                  <div className="patient-nutrition-checks patient-nutrition-checks--stacked">
                    {(() => {
                      const routine = isEditing
                        ? f.daily_routine_anamnesis
                        : parseDailyRoutineAnamnesis(
                            p.fields?.daily_routine_anamnesis?.value,
                          );
                      const selected = normalizeCheckboxKeys(
                        routine?.awakening,
                        DAILY_ROUTINE_AWAKENING_KEYS,
                      );
                      const awakeningTime = routine?.awakening_time ?? "";
                      return DAILY_ROUTINE_AWAKENING_KEYS.map((key) => {
                        if (key === "spontaneous") {
                          return (
                            <div
                              className="patient-nutrition-check patient-nutrition-check--with-hours"
                              key={key}>
                              <label className="patient-nutrition-check">
                                <input
                                  type="checkbox"
                                  checked={selected.includes(key)}
                                  onChange={() =>
                                    handleDailyRoutineMultiToggle(
                                      "awakening",
                                      DAILY_ROUTINE_AWAKENING_KEYS,
                                      key,
                                    )
                                  }
                                />
                                {t("anamnesis.routine.wake.spontaneous")}
                              </label>
                              {isEditing ? (
                                <input
                                  className="patient-nutrition-inline-hours"
                                  name="awakening_time"
                                  value={awakeningTime}
                                  onChange={handleDailyRoutineChange}
                                  placeholder=" "
                                  aria-label={t("anamnesis.labels.awakeningTimeAria")}
                                />
                              ) : (
                                <span className="patient-nutrition-inline-hours-value">
                                  {String(awakeningTime).trim() || "___"}
                                </span>
                              )}
                              <span>{t("anamnesis.labels.hoursShort")}</span>
                            </div>
                          );
                        }
                        return (
                          <label className="patient-nutrition-check" key={key}>
                            <input
                              type="checkbox"
                              checked={selected.includes(key)}
                              onChange={() =>
                                handleDailyRoutineMultiToggle(
                                  "awakening",
                                  DAILY_ROUTINE_AWAKENING_KEYS,
                                  key,
                                )
                              }
                            />
                            {t(DAILY_ROUTINE_AWAKENING_LABEL_KEYS[key])}
                          </label>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-field-label">
                  {t("anamnesis.routine.activityTitle")}:
                </div>
                <div className="patient-nutrition-checks">
                  {DAILY_ROUTINE_ACTIVITY_KEYS.map((key) => {
                    const current = isEditing
                      ? f.daily_routine_anamnesis?.physical_activity
                      : parseDailyRoutineAnamnesis(
                          p.fields?.daily_routine_anamnesis?.value,
                        )?.physical_activity;
                    return (
                      <label className="patient-nutrition-check" key={key}>
                        <input
                          type="checkbox"
                          checked={current === key}
                          onChange={() =>
                            handleDailyRoutineSingleToggle(
                              "physical_activity",
                              key,
                            )
                          }
                        />
                        {t(DAILY_ROUTINE_ACTIVITY_LABEL_KEYS[key])}
                      </label>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="daily-routine-activity-peak">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.routine.activityPeak")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="activity_peak"
                        value={f.daily_routine_anamnesis?.activity_peak ?? ""}
                        onChange={handleDailyRoutineChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseDailyRoutineAnamnesis(
                            p.fields?.daily_routine_anamnesis?.value,
                          )?.activity_peak,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.routine.sports")}:
                  </span>
                  <label className="patient-nutrition-check">
                    <input
                      type="checkbox"
                      checked={
                        (isEditing
                          ? f.daily_routine_anamnesis?.sports
                          : parseDailyRoutineAnamnesis(
                              p.fields?.daily_routine_anamnesis?.value,
                            )?.sports) === "no"
                      }
                      onChange={() =>
                        handleDailyRoutineSingleToggle("sports", "no")
                      }
                    />
                    {t("anamnesis.routine.sportsNo")}
                  </label>
                  <label className="patient-nutrition-check">
                    <input
                      type="checkbox"
                      checked={
                        (isEditing
                          ? f.daily_routine_anamnesis?.sports
                          : parseDailyRoutineAnamnesis(
                              p.fields?.daily_routine_anamnesis?.value,
                            )?.sports) === "yes"
                      }
                      onChange={() =>
                        handleDailyRoutineSingleToggle("sports", "yes")
                      }
                    />
                    {t("anamnesis.routine.sportsYes")}
                  </label>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="daily-routine-stress-level">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.routine.stressLevel")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="stress_level"
                        value={f.daily_routine_anamnesis?.stress_level ?? ""}
                        onChange={handleDailyRoutineChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseDailyRoutineAnamnesis(
                            p.fields?.daily_routine_anamnesis?.value,
                          )?.stress_level,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        {/* Антропометрические показатели */}
        <div className="patient-life-anamnesis-block patient-nutrition-anamnesis-block">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("anamnesis.sections.anthropometry")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="anthropometry-weight-kg">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.anthropometry.weightKg")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="weight_kg"
                        value={f.anthropometry_anamnesis?.weight_kg ?? ""}
                        onChange={handleAnthropometryChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseAnthropometryAnamnesis(
                            p.fields?.anthropometry_anamnesis?.value,
                          )?.weight_kg,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="anthropometry-height-cm">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.anthropometry.heightCm")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="height_cm"
                        value={f.anthropometry_anamnesis?.height_cm ?? ""}
                        onChange={handleAnthropometryChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseAnthropometryAnamnesis(
                            p.fields?.anthropometry_anamnesis?.value,
                          )?.height_cm,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.anthropometry.bodyType")}:
                  </span>
                  <div className="patient-nutrition-checks">
                    {ANTHROPOMETRY_BODY_TYPE_KEYS.map((key) => {
                      const current = isEditing
                        ? f.anthropometry_anamnesis?.body_type
                        : parseAnthropometryAnamnesis(
                            p.fields?.anthropometry_anamnesis?.value,
                          )?.body_type;
                      return (
                        <label className="patient-nutrition-check" key={key}>
                          <input
                            type="checkbox"
                            checked={current === key}
                            onChange={() =>
                              handleAnthropometrySingleToggle("body_type", key)
                            }
                          />
                          {t(ANTHROPOMETRY_BODY_TYPE_LABEL_KEYS[key])}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="anthropometry-weight-fluctuation-3-5">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.anthropometry.fluctuation35")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="weight_fluctuation_3_5_years"
                        value={
                          f.anthropometry_anamnesis
                            ?.weight_fluctuation_3_5_years ?? ""
                        }
                        onChange={handleAnthropometryChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseAnthropometryAnamnesis(
                            p.fields?.anthropometry_anamnesis?.value,
                          )?.weight_fluctuation_3_5_years,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="anthropometry-weight-fluctuation-1">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.anthropometry.fluctuation1")}:
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        className="patient-inline-value"
                        name="weight_fluctuation_1_year"
                        value={
                          f.anthropometry_anamnesis
                            ?.weight_fluctuation_1_year ?? ""
                        }
                        onChange={handleAnthropometryChange}
                        placeholder=" "
                      />
                    ) : (
                      <div className="patient-inline-value">
                        {displayLinedValue(
                          parseAnthropometryAnamnesis(
                            p.fields?.anthropometry_anamnesis?.value,
                          )?.weight_fluctuation_1_year,
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="anthropometry-bmi">
                  <div className="patient-inline-field">
                    <Form.Label className="patient-inline-label">
                      {t("anamnesis.anthropometry.bmi")}:
                    </Form.Label>
                    <div className="patient-inline-value">
                      {displayLinedValue(
                        calcBmi(
                          isEditing
                            ? f.anthropometry_anamnesis?.weight_kg
                            : parseAnthropometryAnamnesis(
                                p.fields?.anthropometry_anamnesis?.value,
                              )?.weight_kg,
                          isEditing
                            ? f.anthropometry_anamnesis?.height_cm
                            : parseAnthropometryAnamnesis(
                                p.fields?.anthropometry_anamnesis?.value,
                              )?.height_cm,
                        ),
                      )}
                    </div>
                  </div>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                  <span className="patient-nutrition-row-label">
                    {t("anamnesis.anthropometry.redistribution")}:
                  </span>
                  <div className="patient-nutrition-checks">
                    {ANTHROPOMETRY_REDISTRIBUTION_KEYS.map((key) => {
                      const current = isEditing
                        ? f.anthropometry_anamnesis?.volume_redistribution
                        : parseAnthropometryAnamnesis(
                            p.fields?.anthropometry_anamnesis?.value,
                          )?.volume_redistribution;
                      return (
                        <label className="patient-nutrition-check" key={key}>
                          <input
                            type="checkbox"
                            checked={current === key}
                            onChange={() =>
                              handleAnthropometrySingleToggle(
                                "volume_redistribution",
                                key,
                              )
                            }
                          />
                          {t(ANTHROPOMETRY_REDISTRIBUTION_LABEL_KEYS[key])}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Показатели */}
        <div className="patient-form-group-general">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">{t("profile.sections.indicators")}</div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3 g-3 align-items-end">
            <Col xs={12} md={5}>
              <div className="patient-form-group">
                <Form.Group controlId="general-biological-age">
                  <Form.Label>{t("profile.fields.biologicalAge")}</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="biological_age"
                      value={f.biological_age}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.biologicalAge")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.biological_age?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
            <Col xs={12} md={5}>
              <div className="patient-form-group">
                <Form.Group controlId="general-cardiovascular-risk">
                  <Form.Label>{t("profile.fields.cvRisk")}</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="cardiovascular_risk"
                      value={f.cardiovascular_risk}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.risk")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.cardiovascular_risk?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
            <Col xs={12} md={2}>
              <div className="patient-form-group patient-profile-indicators-ai">
                <Form.Label className="visually-hidden">{t("profile.aiLabel")}</Form.Label>
                <button
                  type="button"
                  className="patient-profile-indicators-ai__btn"
                  onClick={handleAiAnalysis}
                  disabled={aiLoading}
                  title={t("profile.decodeAi")}
                  aria-label={t("profile.decodeAi")}>
                  {aiLoading ? (
                    <span
                      className="patient-profile-indicators-ai__spinner"
                      aria-hidden="true"
                    />
                  ) : (
                    t("analyses.buttons.ai")
                  )}
                </button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Общая информация */}
        <div className="patient-form-group-general">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("profile.sections.generalInfo")}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-3 g-3">
            {/* <Col md={6}>
              <div className="patient-form-group">
                <Form.Group controlId="general-fio">
                  <Form.Label>{t("profile.fields.fio")}</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="fio"
                      value={f.fio}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.fio")}
                    />
                  ) : (
                    <Form.Control plaintext readOnly value={displayValue(p.fio?.value)} />
                  )}
                </Form.Group>
              </div>
            </Col> */}
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="general-cart">
                  <Form.Label>{t("profile.print.cardNumber")}</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="cart"
                      value={f.cart}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.cart")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.cart?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3 g-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="general-doc">
                  <Form.Label>{t("profile.fields.doctor")}</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      name="doc_id"
                      value={f.doc_id}
                      onChange={handleChange}>
                      <option value="">—</option>
                      {(p?.all_doctors || []).map((d) => (
                        <option key={d?.id} value={String(d?.id ?? "")}>
                          {d?.name ?? "—"}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(
                        p.doc_name?.value?.name ?? p.doc_name?.value,
                      )}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
            {/* <Col md={6}>
              <div className="patient-form-group">
                <Form.Group controlId="general-course">
                  <Form.Label>{t("profile.fields.course")}</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="course"
                      value={f.course}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.course")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayCourseValue(p.course?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col> */}
          </Row>
          {/* Артериальное давление, Объем груди (см), Объем талии (см), Объем бедер (см)   */}
          {/* <Row className="mb-3 g-3">
            <Col md={6}>
              <div className="patient-form-group">
                <Form.Group controlId="general-bp">
                  <Form.Label>{t("profile.fields.bloodPressure")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="blood_pressure"
                      value={f.blood_pressure}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.pressure")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.blood_pressure?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
            <Col md={6}>
              <div className="patient-form-group">
                <Form.Group controlId="general-chest">
                  <Form.Label>{t("profile.fields.chestCircumference")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="chest_circumference_cm"
                      value={f.chest_circumference_cm}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.chestCircumference")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.chest_circumference_cm?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Row className="mb-3 g-3">
            <Col md={6}>
              <div className="patient-form-group">
                <Form.Group controlId="general-waist">
                  <Form.Label>{t("profile.fields.waistCircumference")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="waist_circumference_cm"
                      value={f.waist_circumference_cm}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.waistCircumference")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.waist_circumference_cm?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
            <Col md={6}>
              <div className="patient-form-group">
                <Form.Group controlId="general-hips">
                  <Form.Label>{t("profile.fields.hipsCircumference")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="hips_circumference_cm"
                      value={f.hips_circumference_cm}
                      onChange={handleChange}
                      placeholder={t("profile.placeholders.hipsCircumference")}
                    />
                  ) : (
                    <Form.Control
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.hips_circumference_cm?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row> */}
        </div>

        <div className="patient-diagnosis-complaints-block">
          {/* Диагноз, жалобы */}
          <Row className="mb-3">
            <Col md={12} className="mb-3">
              <div className="patient-form-group">
                <Form.Group controlId="diagnosis">
                  <Form.Label>{t("profile.fields.diagnosis")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="diagnosis"
                      value={f.diagnosis}
                      onChange={handleChange}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.diagnosis?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
            <Col md={12} className="mb-3">
              <div className="patient-form-group">
                <Form.Group controlId="complaints">
                  <Form.Label>{t("profile.fields.complaints")}</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="complaints"
                      value={f.complaints}
                      onChange={handleChange}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.complaints?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        {/* Анамнез заболевания */}
        <div className="patient-form-group-general">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">
                  {t("profile.sections.diseaseAnamnesis")}
                </div>
              </div>
            </Col>
          </Row>
        </div>
        {/* Комментарий врача */}
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="doctor-comment">
                <Form.Label>{t("profile.fields.doctorComment")}</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="doctor_comment"
                    rows={4}
                    value={f.doctor_comment}
                    onChange={handleChange}
                    placeholder={t("profile.placeholders.comment")}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    readOnly
                    plaintext
                    value={displayValue(p.fields?.doctor_comment?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        {/* Сопутствующие заболевания */}
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="cardiovascular-diseases">
                <Form.Label>
                  {t("profile.fields.cardiovascularDiseases")}:
                </Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="cardiovascular_diseases"
                    value={f.cardiovascular_diseases}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(
                      p.fields?.cardiovascular_diseases?.value,
                    )}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="cns-diseases">
                <Form.Label>{t("profile.fields.cnsDiseases")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="cns_diseases"
                    value={f.cns_diseases}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.cns_diseases?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="respiratory-diseases">
                <Form.Label>{t("profile.fields.respiratoryDiseases")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="respiratory_diseases"
                    value={f.respiratory_diseases}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.respiratory_diseases?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="digestive-diseases">
                <Form.Label>{t("profile.fields.digestiveDiseases")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="digestive_diseases"
                    value={f.digestive_diseases}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.digestive_diseases?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="vision-impairments">
                <Form.Label>{t("profile.fields.visionImpairments")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="vision_impairments"
                    value={f.vision_impairments}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.vision_impairments?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        {/* Заболевания щитовидной железы */}
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="thyroid-disease">
                <Form.Label>{t("profile.fields.thyroidDisease")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="thyroid_disease"
                    value={f.thyroid_disease}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.thyroid_disease?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>

        {/* Жалобы old*/}
        {/* <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="complaints">
                <Form.Label>{t("profile.fields.complaints")}</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="complaints"
                    value={f.complaints}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.complaints?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row> */}

        {/* Постоянная терапия */}
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="permanent-therapy">
                <Form.Label>{t("profile.fields.permanentTherapy")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="permanent_therapy"
                    value={f.permanent_therapy}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.permanent_therapy?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>

        {/* Эпикриз, диагноз */}
        <Row className="mb-3">
          {/* <Col md={12} className="mb-3">
              <div className="patient-form-group">
                <Form.Group controlId="medical-summary">
                  <Form.Label>{t("profile.fields.medicalSummary")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      name="medical_summary"
                      value={f.medical_summary}
                      onChange={handleChange}
                      rows={4}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.medical_summary?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col> */}
          {/* Диагноз old */}
          {/* <Col md={12} className="mb-3">
            <div className="patient-form-group">
              <Form.Group controlId="diagnosis">
                <Form.Label>{t("profile.fields.diagnosis")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    name="diagnosis"
                    value={f.diagnosis}
                    onChange={handleChange}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.diagnosis?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col> */}
        </Row>

        {/* Анамнез жизни */}
        <div className="patient-form-group-general">
          <Row className="mb-2">
            <Col md={12}>
              <div className="patient-form-group">
                <div className="patient-profile-group-title">{t("anamnesis.sections.life")}</div>
              </div>
            </Col>
          </Row>
        </div>

        <div className="patient-form-group-general">
          {/* Эпид анамнез */}
          <Row className="mb-3 g-3">
            <Col md={12}>
              <div className="patient-form-group">
                <Form.Group controlId="blood-tests">
                  <Form.Label>{t("profile.fields.epidAnamnesis")}:</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      name="blood_tests"
                      value={f.blood_tests}
                      onChange={handleChange}
                      rows={4}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      plaintext
                      readOnly
                      value={displayValue(p.fields?.blood_tests?.value)}
                    />
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>
        </div>

        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="past-conditions">
                <Form.Label>{t("profile.fields.pastConditions")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="past_conditions"
                    value={f.past_conditions}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.past_conditions?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="allergies">
                <Form.Label>{t("profile.fields.allergies")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="allergies"
                    value={f.allergies}
                    onChange={handleChange}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.allergies?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="heredity">
                <Form.Label>{t("profile.fields.heredity")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="heredity"
                    value={f.heredity}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.heredity?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="bad-habits">
                <Form.Label>{t("profile.fields.badHabits")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="bad_habits"
                    value={f.bad_habits}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.bad_habits?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row>
        {/* Операции */}
        {/* <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="operations">
                <Form.Label>{t("profile.fields.operations")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="operations"
                    value={f.operations}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.operations?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row> */}
        {/* Травмы */}
        {/* <Row className="mb-3">
          <Col md={12}>
            <div className="patient-form-group">
              <Form.Group controlId="injuries">
                <Form.Label>{t("profile.fields.injuries")}:</Form.Label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    name="injuries"
                    value={f.injuries}
                    onChange={handleChange}
                    rows={4}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    plaintext
                    readOnly
                    value={displayValue(p.fields?.injuries?.value)}
                  />
                )}
              </Form.Group>
            </div>
          </Col>
        </Row> */}
      </Form>
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("docs.errors.alertTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            onClick={handleCloseModal}
            className="button-default">
            OK
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAiModal} onHide={handleCloseAiModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("anamnesis.ai.modalTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {aiLoading && (
            <div className="text-muted text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">{t("anamnesis.ai.loading")}</span>
              </div>
            </div>
          )}
          {!aiLoading && aiError && (
            <div className="text-danger">{aiError}</div>
          )}
          {!aiLoading && aiResult != null && (
            <div style={{ whiteSpace: "pre-wrap" }}>{aiResult}</div>
          )}
        </Modal.Body>
        {!aiLoading && (
          <Modal.Footer>
            <button
              type="button"
              onClick={handleCloseAiModal}
              className="button-default">
              {t("anamnesis.ai.close")}
            </button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default PatientProfile;
